/**
 * Some shared helpers for IssuesV2.
 */

import {
  CATEGORY_TO_ISSUE_TYPES,
  Category,
  IgnoredKind,
  IssueScopeKind,
  RISK_ISSUE_TYPES,
} from './common';
import type {
  GlobalScope,
  IssueType,
  LicensingIssueType,
  ProjectScope,
  QualityIssueType,
  ReleaseGroupScope,
  RiskIssueType,
  Scope,
  VulnIssueType,
} from './common';
import type { Project } from './response';

const GLOBAL_SCOPE: GlobalScope = {
  type: IssueScopeKind.Global,
};

export const getGlobalScope = (): GlobalScope => GLOBAL_SCOPE;

export const getProjectScope = (id: string, revision?: string, revisionScanId?: number): ProjectScope => ({
  type: IssueScopeKind.Project,
  id,
  revision,
  revisionScanId,
});

//  Technically these can be strings or numbers, since they are cast to numbers on the BE anyway.
export const getReleaseGroupScope = (id: string, release: number, releaseScanId?: number): ReleaseGroupScope => ({
  type: IssueScopeKind.ReleaseGroup,
  id,
  release,
  releaseScanId,
});

/**
 * Test if a scope is global.
 *
 * @param {Scope} scope - A given scope
 */
export const isGlobalScope = (scope: Scope): scope is GlobalScope => scope.type === 'global';

/**
 * Test if a scope is project.
 *
 * @param {Scope} scope - A given scope.
 */
export const isProjectScope = (scope: Scope): scope is ProjectScope => scope.type === 'project';

/**
 * Test if a scope is releaseGroup.
 *
 * @param {Scope} scope - A given scope.
 */
export const isReleaseGroupScope = (scope: Scope): scope is ReleaseGroupScope => scope.type === 'releaseGroup';

/**
 * API Endpoints used for issues v2 queries.
 */
export const ENDPOINTS = {
  CATEGORY_SUMMARY: '/api/v2/issues/categories',
  TYPES_SUMMARY: '/api/v2/issues/types',
  FETCH_ISSUES: '/api/v2/issues',
  FETCH_ISSUE_LICENSES: '/api/revisions',
  FETCH_REVISIONS: '/api/v2/issues/revisions',
  STATUS_SUMMARY: '/api/v2/issues/statuses',
  LICENSE_LIST: '/api/v2/issues/license-list',
  UPDATE_ISSUES: '/api/v2/issues',
  DOWNLOAD_ISSUES: '/api/v2/issues',
  EXCEPTIONS_LIST: '/api/v2/issues/exceptions',
  PACKAGE_MANAGERS: '/api/v2/issues/package-managers',
  CWES: '/api/v2/issues/cwes',
};

/** Type predicate for testing if an issueType is of LicensingIssueType */
export const isLicensingIssueType = (issueType: IssueType): issueType is LicensingIssueType =>
  CATEGORY_TO_ISSUE_TYPES.licensing.includes(issueType as LicensingIssueType);

/**
 * Type predicate for testing if an issueType is of RiskIssueType
 * Note that risk issues are a subset of quality issues
 */
export const isRiskIssueType = (issueType: IssueType): issueType is RiskIssueType =>
  RISK_ISSUE_TYPES.includes(issueType as RiskIssueType);

/** Type predicate for testing if an issueType is of VulnIssueType */
export const isVulnIssueType = (issueType: IssueType): issueType is VulnIssueType =>
  CATEGORY_TO_ISSUE_TYPES.vulnerability.includes(issueType as VulnIssueType);

/** Type predicate for testing if an issueType is of QualityIssueType */
export const isQualityIssueType = (issueType: IssueType): issueType is QualityIssueType =>
  CATEGORY_TO_ISSUE_TYPES.quality.includes(issueType as QualityIssueType);

//  Determines if any of a given `issue.projects` have been ignored by an ignore rule
export const isAnyIssueProjectIgnoredByRule = (projects: Project[]) => projects.some(
  (issueProject) => issueProject.ignoredKind?.ignoreType === IgnoredKind.IssueException,
);

//  We generally display the "vulnerability" category as "security" to the user.
export const getCategoryDisplayName = (category: Category): Category.Licensing | Category.Quality | 'security' =>
  category === Category.Vulnerability ? 'security' : category;
